/**
 * Sage route: woocommerce-checkout
 *
 * Styles Stripe Elements via the Appearance API (E9.2).
 *
 * Strategy (in priority order):
 *   1. PRIMARY — inject `appearance` into `window.wc_stripe_upe_params` before
 *      the UPE gateway bootstraps. The plugin reads that property at Elements
 *      creation time; if present it skips generating the default appearance.
 *   2. FALLBACK — inject a <style> tag that targets `.wc-stripe-upe-element`
 *      and `.StripeElement` wrapper divs (border, background, focus ring).
 *      This covers the outer container even when the iframe content is fixed.
 */

const appearance = {
  theme: 'flat',
  variables: {
    // Navy, not brand yellow: Stripe uses colorPrimary for text-like accents
    // (the Link banner, selected tabs) where yellow-on-white is illegible.
    // Yellow focus styling is preserved by the explicit .Input:focus rule.
    colorPrimary: '#18243c',
    colorBackground: '#ffffff',
    colorText: '#18243c',
    colorDanger: '#d9363e',
    fontFamily: '"Inter", system-ui, sans-serif',
    borderRadius: '6px',
    spacingUnit: '4px',
    // Breathing room between field rows (expiry/CVC row was touching the
    // card-number field above it).
    spacingGridRow: '16px',
    spacingGridColumn: '16px',
  },
  rules: {
    '.Input': {
      border: '1px solid #d1d5db',
      padding: '12px',
      fontSize: '15px',
      boxShadow: 'none',
    },
    '.Input:focus': {
      border: '1px solid #fcbf00',
      boxShadow: '0 0 0 3px rgba(252,191,0,0.25)',
    },
    '.Input::placeholder': {
      color: '#9ca3af',
    },
    // Match the site's form labels (text-sm font-medium text-gray-700).
    '.Label': {
      color: '#374151',
      fontWeight: '500',
      fontSize: '14px',
    },
  },
};

/**
 * Attempt to inject appearance into the WC Stripe UPE params object.
 * Returns true if the params object was found and patched.
 */
const hookIntoStripeParams = () => {
  // wc_stripe_upe_params is wp_localize_script'd onto the page
  if (window.wc_stripe_upe_params) {
    window.wc_stripe_upe_params.appearance = appearance;
    // Also patch blocksAppearance so block-checkout mode is covered
    window.wc_stripe_upe_params.blocksAppearance = appearance;
    return true;
  }

  // Fallback: the plugin can also source params from wc.wcSettings
  if (window.wc && window.wc.stripe) {
    try {
      const params = window.wc.stripe;
      if (params && typeof params === 'object') {
        params.appearance = appearance;
        return true;
      }
    } catch (e) {
      // swallow — not critical
    }
  }

  return false;
};

/**
 * Inject a CSS fallback that styles the Stripe element WRAPPER div.
 * This improves visual consistency even when the iframe content itself
 * uses Stripe defaults.
 */
const injectWrapperStyles = () => {
  if (document.getElementById('metreel-stripe-wrapper-styles')) return;

  const css = `
    /* Metreel – Stripe UPE wrapper. The Appearance API styles the fields
       inside the iframe (borders, focus ring), so the wrapper must add no
       chrome of its own — a second border double-boxes the whole element. */
    .wc-stripe-upe-element,
    .StripeElement {
      border: none !important;
      padding: 0 !important;
      background: transparent !important;
    }
    /* Gateway description (e.g. Stripe's test-mode blurb) rendered above the
       payment element — quieten it to caption text. */
    .payment_box > p {
      font-size: 0.8125rem !important;
      line-height: 1.5 !important;
      color: #6b7280 !important;
      margin: 0 0 0.75rem !important;
    }
    /* Label above the payment element */
    .wc-stripe-upe-element + label,
    #payment .wc-stripe-upe-element-tab label {
      color: #18243c !important;
      font-weight: 500 !important;
    }
  `.trim();

  const style = document.createElement('style');
  style.id = 'metreel-stripe-wrapper-styles';
  style.textContent = css;
  document.head.appendChild(style);
};

const syncAddressToggle = () => {
  const checkbox = document.getElementById('ship-to-different-address-checkbox');
  const billingChoice = document.querySelector('[data-checkout-address-choice="billing"]');
  const options = document.querySelectorAll('.checkout-address-option');

  if (!checkbox) return;

  const refreshActiveState = () => {
    options.forEach((option) => option.classList.remove('checkout-address-option--active'));

    const activeSelector = checkbox.checked
      ? 'label[for="ship-to-different-address-checkbox"]'
      : 'label[for="checkout-use-billing-address"]';
    const active = document.querySelector(activeSelector);
    if (active) active.classList.add('checkout-address-option--active');

    if (billingChoice) billingChoice.checked = !checkbox.checked;
  };

  if (billingChoice && billingChoice.dataset.checkoutAddressBound !== 'true') {
    billingChoice.dataset.checkoutAddressBound = 'true';
    billingChoice.addEventListener('change', () => {
      if (!billingChoice.checked || !checkbox.checked) return;
      checkbox.checked = false;
      window.jQuery(checkbox).trigger('change');
      refreshActiveState();
    });
  }

  if (checkbox.dataset.checkoutAddressBound !== 'true') {
    checkbox.dataset.checkoutAddressBound = 'true';
    checkbox.addEventListener('change', refreshActiveState);
  }
  refreshActiveState();
};

const ensurePlaceOrderIcon = () => {
  const button = document.getElementById('place_order');
  if (!button) return;

  const text = button.textContent.trim() || button.value || button.dataset.value || 'Place order';
  if (!button.querySelector('span')) {
    button.textContent = '';
    const label = document.createElement('span');
    label.textContent = text;
    button.appendChild(label);
  }

  if (!button.querySelector('i.fa-arrow-right, svg.fa-arrow-right')) {
    const icon = document.createElement('i');
    icon.className = 'fa fa-arrow-right';
    icon.setAttribute('aria-hidden', 'true');
    button.appendChild(icon);
  }
};

export default {
  init() {
    // Bail on non-checkout pages
    if (!document.querySelector('.woocommerce-checkout')) return;

    // Init can be reached twice (directly from common.js and via the
    // body-class router) — run once so the updated_checkout handlers
    // below don't double-bind.
    if (document.body.dataset.metreelCheckoutInit === 'true') return;
    document.body.dataset.metreelCheckoutInit = 'true';

    // WC's checkout refresh uses blockUI around shipping-method changes.
    // Make it snap rather than fade so the order summary feels stable.
    if (window.jQuery && window.jQuery.blockUI && window.jQuery.blockUI.defaults) {
      window.jQuery.blockUI.defaults.fadeIn = 0;
      window.jQuery.blockUI.defaults.fadeOut = 0;
    }

    // Always inject wrapper-level CSS as a guaranteed baseline
    injectWrapperStyles();
    syncAddressToggle();
    ensurePlaceOrderIcon();

    // Attempt to hook into Stripe params — poll until the gateway script
    // has run its wp_localize_script block (up to 20 × 150 ms = 3 s).
    let attempts = 0;
    const MAX_ATTEMPTS = 20;
    const INTERVAL_MS = 150;

    const tryHook = () => {
      attempts++;
      const patched = hookIntoStripeParams();

      if (patched) {
        // Success — params patched before Elements mount
        return;
      }

      if (attempts < MAX_ATTEMPTS) {
        setTimeout(tryHook, INTERVAL_MS);
      }
      // If max attempts reached, wrapper CSS fallback is already in place — silent no-op.
    };

    tryHook();

    // Re-apply on AJAX checkout refresh (WC fires this after form updates).
    // The UPE re-mounts its element after updated_checkout; re-patch params
    // so the next mountPaymentElement call picks up our appearance.
    jQuery(document.body).on('updated_checkout', () => {
      hookIntoStripeParams();
      syncAddressToggle();
      ensurePlaceOrderIcon();
    });

    jQuery(document.body).on('payment_method_selected', () => {
      ensurePlaceOrderIcon();
    });
  },

  finalize() {
    // Nothing needed here
  },
};



// WEBPACK FOOTER //
// ./scripts/routes/woocommerceCheckout.js